namespace Solovis.Reports {

  export interface ParameterSetOptions {
    parameters?: any;
    portalServices?: Solovis.Portal.IPortalServices;
  }

  /**
   * ParameterSet will manage a set of parameters for a report and can load required additional
   * values (such as label and resourceId for an entityId on an entity parameter).
   */
  export class ParameterSet {

    public _paramInfos;
    public parameters;
    public _paramNames;
    public _paramNameToHolderMap: Record<string, any>;

    public areAllParametersReady: boolean;

    private _portalServices: Solovis.Portal.IPortalServices;
    private _subscriptions = {};


    constructor(options: ParameterSetOptions) {
      var self = this;

      if (options.portalServices) {
        this._portalServices = options.portalServices;
      }
      else {
        this._portalServices = new Solovis.Portal.SitePortalServices();
      }

      this._paramInfos = options.parameters ? options.parameters : {};
      this.parameters = this._paramInfos;

      this._paramNames = [];
      this._paramNameToHolderMap = {};

      // preloadRequired will be set to true if any of the parameters require preloading.
      var preloadRequired = false;

      $(this._paramInfos).each(function (i, paramInfo: any) {
        if (paramInfo) {
          var holder: any = {
            paramInfo: paramInfo,
            name: paramInfo.name,
            value: Solovis.isNotNull(paramInfo.initialValue) ? paramInfo.initialValue : null,
            isList: paramInfo.list ? true : false
          };

          // Determine parameter edit control type.
          var paramEditControlType = null;
          if (paramInfo.parameterControl) {
            paramEditControlType = paramInfo.parameterControl;
          }
          else if (paramInfo.parameterType) {
            paramEditControlType = Solovis.Reports.ParameterTypes.getParameterControlForParameterType(paramInfo.parameterType);
          }
          else {
            throw "No parameterType or parameterControl specified for parameter: " + paramInfo.name;
          }

          if (typeof paramEditControlType == "string") {
            var paramEditControlTypeName = paramEditControlType;
            paramEditControlType = Solovis.resolveObject(paramEditControlTypeName);
            if (!paramEditControlType) {
              throw "Unable to load parameter control: " + paramEditControlTypeName;
            }
          }

          holder.paramEditControlType = paramEditControlType;

          holder.needsPreload = function (paramValue) {
            if (paramEditControlType.preload) {
              // preload is an optional static method on the paramEditControlType.
              if (paramValue !== null && (!paramEditControlType.shouldPreload || paramEditControlType.shouldPreload(paramValue))) {
                // If the paramEditControlType has preload method and value is not null, we will need to preload the value (unless
                // the paramEditControlType is able to tell us that preloading is not needed).                                
                return true;
              }
            }

            return false;
          };

          if (self._isPreloadNeededForParamHolder(holder, holder.value)) {
            preloadRequired = true;
            holder.preloadRequired = true;
          }

          if (!self._paramNameToHolderMap[paramInfo.name]) {
            self._paramNameToHolderMap[paramInfo.name] = holder;
            self._paramNames.push(paramInfo.name);
          }
          else {
            throw "Duplicate parameter name: " + paramInfo.name;
          }
        }
      });

      this.areAllParametersReady = !preloadRequired;

      if (preloadRequired) {
        var handleParamPreloaded = function (holder) {
          holder.preloadRequired = false;

          var foundOneNotReady = false;
          $(self._paramNames).each(function (i, paramName: any) {
            var holder = self._paramNameToHolderMap[paramName];
            if (holder.preloadRequired) {
              foundOneNotReady = true;
            }
          });

          if (!foundOneNotReady) {
            // We have now preloaded all parameters.
            self._notifyAllParametersReady();
          }
        };

        $(self._paramNames).each(function (i, paramName: any) {
          var holder = self._paramNameToHolderMap[paramName];
          if (holder.preloadRequired) {
            self._preloadValueForParamHolder(holder, holder.value, function () { handleParamPreloaded(holder) });
          }
        });
      }
    }


    public tryGetParameterInfoByName(paramName: string) {
      var paramHolder = this._paramNameToHolderMap[paramName];
      if (paramHolder) {
        return paramHolder.paramInfo;
      }

      return null;
    }


    public _isPreloadNeededForParamHolder(paramHolder, value) {
      if (!paramHolder.isList) {
        if (paramHolder.needsPreload(value)) {
          return true;
        }
      }
      else {
        if (Solovis.isNotNull(value)) {
          // We need to preload if at least one value in the list needs to be preloaded.
          for (var i = 0; i < value.length; i++) {
            var item = value[i];
            if (paramHolder.needsPreload(item)) {
              return true;
            }
          }
        }
      }

      return false;
    };

    public _preloadValueForParamHolder(paramHolder, value, callback) {
      var paramEditControlType = paramHolder.paramEditControlType;

      // preload callback will be passed response with full parameter value in the result.
      if (!paramHolder.isList) {
        paramEditControlType.preload(
          value,
          function (loadedValue, success) {
            if (success) {
              paramHolder.value = loadedValue;
              callback(true);
            }
            else {
              // Determine best way to report this.
              console.log("preloading parameter value failed: " + paramHolder.name);
              console.log(value);
              callback(false);
            }
          },
          this._portalServices);
      }
      else {
        var preloadedValues = [];
        var preloadValueForIndex = (list, i) => {
          if (list && i < list.length) {
            var listItem = list[i];
            paramEditControlType.preload(
              listItem,
              function (loadedValue, success) {
                if (success) {
                  preloadedValues.push(loadedValue);
                }
                else {
                  // Determine best way to report this.
                  console.log("preloading parameter value failed: " + paramHolder.name);
                  console.log(listItem);
                }

                // Load the next value.
                preloadValueForIndex(list, i + 1);
              },
              this._portalServices
            );
          }
          else {
            paramHolder.value = preloadedValues;
            callback(true);
          }
        };

        if (paramEditControlType.hasOwnProperty('preloadMultiple')) {
          paramEditControlType.preloadMultiple(
            value,
            function (loadedValue: any[], success: boolean) {
              if (success) {
                paramHolder.value = loadedValue;
                callback(true);
              }
              else {
                preloadValueForIndex(value, 0);
              }
            },
            this._portalServices)
        }
        else {
          preloadValueForIndex(value, 0);
        }
      }
    }


    /**
     * Each named property of paramValue will be used to call setParameterValue with name and paramValues[name] as value.
     * @param paramValues
     */
    public setParameterValues(paramValues) {
      for (var paramName in paramValues) {
        var value = paramValues[paramName];
        this.setParameterValue(paramName, value);
      }
    }


    public setParameterValue(paramName, paramValue) {
      var self = this;

      if (this._paramNameToHolderMap[paramName]) {
        var holder = this._paramNameToHolderMap[paramName];

        if (this._isPreloadNeededForParamHolder(holder, paramValue)) {
          this._preloadValueForParamHolder(
            holder, paramValue,
            function (success) {
              self._notifyParameterValueChanged(holder.name);
            });
        }
        else {
          holder.value = paramValue;
          this._notifyParameterValueChanged(holder.name);
        }
      }
      else {
        console.log("unknown parameter name: " + paramName);
      }
    };


    /**
     * This should only be called from a parameter editor control to change the value to a fully populated parameter value (which will never need preloading).
     * @param paramName
     * @param paramValue
     * @param source
     */
    public internalSetFullParameterValue(paramName, paramValue, source) {
      if (this._paramNameToHolderMap[paramName]) {
        var holder = this._paramNameToHolderMap[paramName];
        holder.value = paramValue;

        this._notifyParameterValueChanged(holder.name, source);
      }
      else {
        console.log("unknown parameter name: " + paramName);
      }
    }

    public getParameterValue(paramName) {
      if (this._paramNameToHolderMap[paramName]) {
        var holder = this._paramNameToHolderMap[paramName];
        return holder.value;
      }
      else {
        console.log("unknown parameter name: " + paramName);
        return null;
      }
    }

    public getParameterValues(): any {
      var self = this;

      var params: any = {};

      $(this._paramNames).each(function (i, paramName: any) {
        var value = self.getParameterValue(paramName);
        params[paramName] = value;
      });

      return params;
    }

    public getSerializedParameterValues() {
      var self = this;

      var params = {};

      $(this._paramNames).each(function (i, paramName: any) {
        var holder = self._paramNameToHolderMap[paramName];
        var value = holder.value;

        if (holder.paramEditControlType.serializeParameterValue) {
          if (!holder.isList) {
            value = holder.paramEditControlType.serializeParameterValue(value);
          }
          else {
            var valueList = [];

            $(value).each(function (i, item) {
              var serializedValue = holder.paramEditControlType.serializeParameterValue(item);
              valueList.push(serializedValue);
            });

            value = valueList;
          }
        }

        params[paramName] = value;
      });

      return params;
    }

    public _notifyParameterValueChanged(paramName, source?) {
      // This will be called whenever a parameter value is changed. Source will be the parameter list
      // control when the edit came from the user interface. Passing source with this event
      // will allow us to ignore the change from the perspective of updating the user interface.
      $(this).trigger("parameterValueChanged", { name: paramName, source: (source ? source : null) });

      if (Array.isArray(this._subscriptions[paramName])) {
        this._subscriptions[paramName].forEach(({ subscriptionHandler, paramHolder, unmountHandler }) => {
          unmountHandler instanceof Function && unmountHandler();
          subscriptionHandler(paramHolder.value); // pass whole object?
        });
        this._subscriptions[paramName] = this._subscriptions[paramName].filter(({ shouldUnmount }) => !shouldUnmount);
      }
    }

    public _notifyAllParametersReady() {
      // This will be called whenever the parameters are ready.
      this.areAllParametersReady = true;
      $(this).trigger("allParametersReady");
    }


    public static preloadParameterValuesOfOneType(parameterType, values, callback, portalServices?) {
      var parameters = [];
      parameters.push({ name: "values", label: "Values", parameterType: parameterType, list: true, initialValue: values });

      var parameterSet = new Solovis.Reports.ParameterSet({
        parameters: parameters,
        portalServices: portalServices
      });

      $(parameterSet).bind("allParametersReady", function () {
        var paramValues = parameterSet.getParameterValues();
        var response = {
          result: paramValues.values
        };
        callback(response, true);
      });
    }

    /**
     * @param argument.paramName parameter to watch
     * @param argument.subscriptionHandler function to run, when paramName changes
     * @param argument.shouldUnmount remove subscriber from subscription list
     * @param argument.unmountHandler optional, runs in case we want to cleanup subscriber "state"
     */
    public listenForParameterChange = ({ paramName, subscriptionHandler, shouldUnmount = false, unmountHandler }: { paramName: 'string', subscriptionHandler: (paramHolder: Record<string, any>) => void, shouldUnmount: boolean, unmountHandler?: () => void }) => {
      const paramHolder = this._paramNameToHolderMap[paramName];
      if (typeof paramHolder === 'object') {
        const subscription = { subscriptionHandler, paramHolder, shouldUnmount, unmountHandler };
        if (Array.isArray(this._subscriptions[paramName])) {
          this._subscriptions[paramName] = this._subscriptions[paramName].concat(subscription);
        } else {
          this._subscriptions[paramName] = [subscription];
        }
      }
    }
  }
}
